import React, { useState, useRef, useEffect, useCallback } from 'react';
import useUploadContext from "hooks/useUploadContext";
import './index.scss';

const MainContainer = () => {
    const {
        totalTime,
        leftBoundary,
        setLeftBoundary,
        rightBoundary,
        setRightBoundary,
        time,
        bares,
    } = useUploadContext();

    const [dragging, setDragging] = useState(null);
    const rulerRef = useRef(null);
    const leftRef = useRef(leftBoundary);
    const rightRef = useRef(rightBoundary);
    const posRef = useRef(0);
    const rectRef = useRef(null);
    const waveShapeRef = useRef(null);

    const updateRectangle = useCallback(() => {
        if (rectRef.current) {
            rectRef.current.style.left = `${(leftRef.current / totalTime) * 100}%`;
            rectRef.current.style.width = `${((rightRef.current - leftRef.current) / totalTime) * 100}%`;
        }
    }, [totalTime]);

    useEffect(() => {
        leftRef.current = leftBoundary;
        rightRef.current = rightBoundary;
        updateRectangle();
    }, [leftBoundary, rightBoundary, updateRectangle]);

    const getTime = (time) => {
        const value = time / 100;
        const minutes = Math.floor(value / 60);
        const seconds = Math.floor((value % 60));
        let milisecs = (value % 1).toString().split('.')[1] || '00';
        milisecs = milisecs.slice(0, 2);
        if (milisecs.length < 2) {
            milisecs = milisecs.padEnd(2, '0');
        }
        return `${`0${minutes}`.slice(-2)}:${`0${seconds}`.slice(-2)}:${milisecs}`;
    };

    const handleMouseDown = (side) => (event) => {
        event.preventDefault();
        const clientX = event.type === 'touchstart' ? event.touches[0].clientX : event.clientX;
        setDragging(side);
        posRef.current = clientX;
    };

    const handleMouseMove = (event) => {
        if (!dragging) return;

        const clientX = event.type === 'touchmove' ? event.touches[0].clientX : event.clientX;

        const rulerRect = rulerRef.current.getBoundingClientRect();
        const toLeftRuler = (clientX - rulerRect.left);
        const newValue = (toLeftRuler / rulerRect.width) * totalTime;

        if (dragging === 'left') {
            if (newValue < rightRef.current && newValue >= 0 && Math.abs(newValue - rightRef.current) >= 30) {
                leftRef.current = newValue;
                updateRectangle();
            }
        } else if (dragging === 'right') {
            if (newValue > leftRef.current && newValue <= totalTime && Math.abs(newValue - leftRef.current) >= 30) {
                rightRef.current = newValue;
                updateRectangle();
            }
        } else if (dragging === 'boundary') {
            const diff = ((clientX - posRef.current) / rulerRect.width) * totalTime;
            const newLeft = leftRef.current + diff;
            const newRight = rightRef.current + diff;
            if (newLeft >= 0 && newRight <= totalTime) {
                leftRef.current = newLeft;
                rightRef.current = newRight;
                updateRectangle();
            }
            posRef.current = clientX;
        }
    };

    const handleMouseUp = () => {
        if (dragging) {
            setDragging(null);
            setLeftBoundary(leftRef.current);
            setRightBoundary(rightRef.current);
        }
    };

    useEffect(() => {
        const standardTotalTime = Math.floor(totalTime * 100);
        waveShapeRef.current = (
            Array.from({ length: standardTotalTime + 1 }, (_, i) => (
                <div key={i} className="mark" style={{ left: `${(i / standardTotalTime) * 100}%` }}>
                    <span className={i % 6000 === 0 ? 'minute' : i % 1000 === 0 ? 'second' : i % 100 === 0 ? 'milisec' : ''}></span>
                    {((i % 6000 === 0) || (i === standardTotalTime)) && (<span className="label"> {getTime(i)}</span>)}
                </div>
            ))
        );
    }, [totalTime]);

    return (
        <div
            className="time-ruler-wrapper"
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchMove={handleMouseMove}
            onTouchEnd={handleMouseUp}
            onTouchCancel={handleMouseUp}
        >
            <div className="ruler" ref={rulerRef}>
                {!!totalTime && (
                    <>
                        <div className="marks">
                            {waveShapeRef.current}
                        </div>
                        <div
                            className="rectangle"
                            ref={rectRef}
                            style={{
                                left: `${(leftRef.current / totalTime) * 100}%`,
                                width: `${((rightRef.current - leftRef.current) / totalTime) * 100}%`
                            }}
                        >
                            <div
                                className="handle"
                                onMouseDown={handleMouseDown('left')}
                                onTouchStart={handleMouseDown('left')}
                            >
                                <div className='down-arrow'></div>
                            </div>
                            <div
                                className="selected-boundary"
                                onMouseDown={handleMouseDown('boundary')}
                                onTouchStart={handleMouseDown('boundary')}
                            ></div>
                            <div
                                className="handle"
                                onMouseDown={handleMouseDown('right')}
                                onTouchStart={handleMouseDown('right')}
                            >
                                <div className='down-arrow'></div>
                            </div>
                        </div>
                        {
                            totalTime &&
                            <div
                                className="time-indicator"
                                style={{
                                    left: `${(time / totalTime) * 100}%`,
                                }}
                            ></div>
                        }
                        {
                            totalTime && bares.map((bare, i) => (
                                (bare > leftBoundary && bare < rightBoundary) &&
                                <div
                                    key={i}
                                    className="bare"
                                    style={{
                                        left: `${(bare / totalTime) * 100}%`,
                                    }}
                                >
                                </div>
                            ))
                        }
                    </>
                )}
            </div>
        </div>
    );
};

export default MainContainer;
